/**
 * Custom dashboard canvas helpers (MT-1666) — shared by the web canvas, the
 * server-side PDF / email renderers and the delivery scheduler so every surface
 * places, orders and groups elements identically.
 */
import { DashboardCardType, DashboardScheduleCadence } from '../enums.js';
import type {
  DashboardCard,
  DashboardCardRect,
  DashboardMetricSeries,
  DashboardSchedule,
  DashboardScheduleInput,
} from '../types/custom-dashboard.js';

/** Canvas width in grid columns. */
export const DASHBOARD_GRID_COLS = 12;
/** Maximum element height in grid rows (mirrors chk_dashboard_card_size). */
export const DASHBOARD_MAX_ROWS = 40;
/** Height of one grid row in CSS px on the web canvas. */
export const DASHBOARD_ROW_PX = 32;

/** Default size of a newly added element, by type. */
export const DASHBOARD_DEFAULT_SIZE: Record<DashboardCardType, { w: number; h: number }> = {
  [DashboardCardType.Metric]: { w: 4, h: 7 },
  [DashboardCardType.MetricValue]: { w: 3, h: 4 },
  [DashboardCardType.Sparkline]: { w: 3, h: 5 },
  [DashboardCardType.Objective]: { w: 4, h: 4 },
  [DashboardCardType.Stat]: { w: 3, h: 4 },
  [DashboardCardType.Title]: { w: 12, h: 2 },
  [DashboardCardType.Frame]: { w: 12, h: 9 },
};

/** Stable identity of a metric-entry series (entity + scope). */
export function dashboardSeriesKey(s: DashboardMetricSeries): string {
  return [s.measurement_entity_id, s.department_id ?? '', s.project_group_id ?? '', s.project_id ?? ''].join('|');
}

export function clampRect(r: DashboardCardRect): DashboardCardRect {
  const w = Math.min(DASHBOARD_GRID_COLS, Math.max(1, Math.round(r.w)));
  const h = Math.min(DASHBOARD_MAX_ROWS, Math.max(1, Math.round(r.h)));
  const x = Math.min(DASHBOARD_GRID_COLS - w, Math.max(0, Math.round(r.x)));
  const y = Math.max(0, Math.round(r.y));
  return { id: r.id, x, y, w, h };
}

type PlaceableCard = Pick<DashboardCard, 'id' | 'pos_x' | 'pos_y' | 'col_span' | 'row_span' | 'sort_order'>;

/**
 * Resolves every card to a grid rectangle. Cards with a stored position keep
 * it; cards without one (legacy dashboards) flow left-to-right, top-to-bottom
 * in sort_order into the first free slot below/around the placed ones.
 */
export function resolveCardRects(cards: PlaceableCard[]): Map<string, DashboardCardRect> {
  const rects = new Map<string, DashboardCardRect>();
  const occupied = new Set<string>();
  const mark = (r: DashboardCardRect) => {
    for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) occupied.add(`${x},${y}`);
  };
  const fits = (x: number, y: number, w: number, h: number) => {
    for (let yy = y; yy < y + h; yy++) for (let xx = x; xx < x + w; xx++) if (occupied.has(`${xx},${yy}`)) return false;
    return true;
  };

  for (const c of cards) {
    if (c.pos_x == null || c.pos_y == null) continue;
    const r = clampRect({ id: c.id, x: c.pos_x, y: c.pos_y, w: c.col_span, h: c.row_span });
    rects.set(c.id, r);
    mark(r);
  }

  const unplaced = cards
    .filter(c => c.pos_x == null || c.pos_y == null)
    .sort((a, b) => a.sort_order - b.sort_order);
  for (const c of unplaced) {
    const w = Math.min(DASHBOARD_GRID_COLS, Math.max(1, c.col_span));
    const h = Math.min(DASHBOARD_MAX_ROWS, Math.max(1, c.row_span));
    let placed: DashboardCardRect | null = null;
    for (let y = 0; !placed; y++) {
      for (let x = 0; x + w <= DASHBOARD_GRID_COLS; x++) {
        if (fits(x, y, w, h)) { placed = { id: c.id, x, y, w, h }; break; }
      }
    }
    rects.set(c.id, placed);
    mark(placed);
  }
  return rects;
}

/** Total canvas height in rows. */
export function canvasRows(rects: Iterable<DashboardCardRect>): number {
  let rows = 0;
  for (const r of rects) rows = Math.max(rows, r.y + r.h);
  return rows;
}

/** True when `inner` lies entirely inside `outer`. */
export function rectContains(outer: DashboardCardRect, inner: DashboardCardRect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y
    && inner.x + inner.w <= outer.x + outer.w
    && inner.y + inner.h <= outer.y + outer.h;
}

const byReadingOrder = (a: DashboardCardRect, b: DashboardCardRect) => a.y - b.y || a.x - b.x;

export interface DashboardSection<C> {
  /** The frame grouping these cards; null for top-level elements */
  frame: C | null;
  cards: C[];
}

/**
 * Linear reading order for surfaces that cannot reproduce the canvas (email,
 * mobile): frames become sections holding the elements placed inside them;
 * other elements stand alone. Sections and their cards run top-to-bottom,
 * left-to-right.
 */
export function dashboardSections<C extends PlaceableCard & { card_type: DashboardCardType }>(
  cards: C[],
): DashboardSection<C>[] {
  const rects = resolveCardRects(cards);
  const rectOf = (c: C) => rects.get(c.id)!;
  const frames = cards.filter(c => c.card_type === DashboardCardType.Frame)
    .sort((a, b) => byReadingOrder(rectOf(a), rectOf(b)));
  const owner = new Map<string, C>();
  for (const c of cards) {
    if (c.card_type === DashboardCardType.Frame) continue;
    // Innermost (smallest) containing frame wins
    const containing = frames.filter(f => rectContains(rectOf(f), rectOf(c)))
      .sort((a, b) => rectOf(a).w * rectOf(a).h - rectOf(b).w * rectOf(b).h);
    if (containing.length) owner.set(c.id, containing[0]);
  }

  const items: { rect: DashboardCardRect; section: DashboardSection<C> }[] = [];
  for (const f of frames) {
    const inner = cards.filter(c => owner.get(c.id)?.id === f.id)
      .sort((a, b) => byReadingOrder(rectOf(a), rectOf(b)));
    items.push({ rect: rectOf(f), section: { frame: f, cards: inner } });
  }
  for (const c of cards) {
    if (c.card_type === DashboardCardType.Frame || owner.has(c.id)) continue;
    items.push({ rect: rectOf(c), section: { frame: null, cards: [c] } });
  }
  return items.sort((a, b) => byReadingOrder(a.rect, b.rect)).map(i => i.section);
}

// ── Delivery schedules ─────────────────────────────────────────────

export interface ZonedParts {
  /** ISO day of week (1=Monday … 7=Sunday) */
  isoDow: number;
  day: number;
  hour: number;
  /** Local calendar date, YYYY-MM-DD */
  date: string;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Calendar parts of `now` in an IANA time zone (falls back to UTC when invalid). */
export function zonedParts(now: Date, timeZone: string): ZonedParts {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', hourCycle: 'h23',
    }).formatToParts(now);
  } catch {
    return {
      isoDow: ((now.getUTCDay() + 6) % 7) + 1,
      day: now.getUTCDate(),
      hour: now.getUTCHours(),
      date: now.toISOString().slice(0, 10),
    };
  }
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  return {
    isoDow: WEEKDAYS.indexOf(get('weekday')) + 1,
    day: Number(get('day')),
    hour: Number(get('hour')) % 24,
    date: `${get('year')}-${get('month')}-${get('day')}`,
  };
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** Minimum gap between two deliveries of one schedule — guards against a double tick in the same hour. */
export const DASHBOARD_DELIVERY_MIN_GAP_MS = 50 * 60 * 1000;

type ScheduleTiming = Pick<DashboardSchedule, 'cadence' | 'day_of_week' | 'day_of_month' | 'hour' | 'timezone' | 'last_sent_at'>;

/**
 * Hours after a schedule's own local hour (on the same local day) in which a
 * delivery that hasn't gone out yet still fires — so a failed delivery, whose
 * claim the scheduler releases, is retried by the next hourly tick(s), and a
 * missed tick catches up. The same-local-day check below keeps it to one a day.
 */
export const DASHBOARD_DELIVERY_RETRY_HOURS = 3;

/** True when the schedule should fire during the hour containing `now`. */
export function isScheduleDue(s: ScheduleTiming, now: Date = new Date()): boolean {
  const p = zonedParts(now, s.timezone);
  if (p.hour < s.hour || p.hour > s.hour + DASHBOARD_DELIVERY_RETRY_HOURS) return false;
  if (s.cadence === DashboardScheduleCadence.Weekly && p.isoDow !== s.day_of_week) return false;
  if (s.cadence === DashboardScheduleCadence.Monthly && p.day !== s.day_of_month) return false;
  if (s.last_sent_at) {
    if (now.getTime() - new Date(s.last_sent_at).getTime() < DASHBOARD_DELIVERY_MIN_GAP_MS) return false;
    // At most one delivery per local day: when clocks fall back the same local
    // hour occurs twice, an hour apart — the second must not deliver again.
    if (zonedParts(new Date(s.last_sent_at), s.timezone).date === p.date) return false;
  }
  return true;
}

const ISO_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function hourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00 ${hour < 12 ? 'AM' : 'PM'}`;
}

export type DashboardScheduleTiming = Pick<DashboardScheduleInput, 'cadence' | 'day_of_week' | 'day_of_month' | 'hour' | 'timezone'>;

/** Human summary, e.g. "Weekly on Monday at 8:00 AM (America/New_York)". */
export function describeSchedule(s: DashboardScheduleTiming): string {
  const at = `at ${hourLabel(s.hour)} (${s.timezone})`;
  switch (s.cadence) {
    case DashboardScheduleCadence.Weekly:
      return `Weekly on ${ISO_DAY_NAMES[(s.day_of_week ?? 1) - 1]} ${at}`;
    case DashboardScheduleCadence.Monthly:
      return `Monthly on the ${ordinal(s.day_of_month ?? 1)} ${at}`;
    default:
      return `Daily ${at}`;
  }
}

export const DASHBOARD_ISO_DAY_NAMES = ISO_DAY_NAMES;
